<script setup lang="ts">
import { ROUTE_NAMES } from '~/constants/routes';

// COMPOSABLES
const { tt } = useTypedI18n();
useSeoMeta({
    title: tt('seo.sell.title'),
    ogTitle: tt('seo.sell.title'),
    description: tt('seo.sell.description'),
    ogDescription: tt('seo.sell.description'),
});
const localePath = useLocalePath();

// DATA
const isSuccess = ref<boolean>(false);
</script>

<template>
    <div class="mb-12 flex flex-col gap-y-12 sm:mb-16 sm:gap-y-16 xl:mb-28 xl:gap-y-28">
        <SellPageHeroBlock
            id="request"
            class="scroll-my-[calc(var(--header-total-height)+1rem)]"
            :title="tt('sell.hero.title')"
            :description="tt('sell.hero.description')"
            :items="[tt('sell.hero.items.item1'), tt('sell.hero.items.item2'), tt('sell.hero.items.item3')]"
        >
            <SellPageForm v-if="isSuccess === false" class="flex-1" @success="isSuccess = true" />
            <Transition
                v-else
                enter-from-class="opacity-0"
                leave-to-class="opacity-0"
                enter-active-class="transition-opacity duration-300"
                leave-active-class="transition-opacity duration-300"
            >
                <SellPageSuccessBlock
                    class="flex-1"
                    :title="tt('sell.success.title')"
                    :description="tt('sell.success.description')"
                    :guest-title="tt('sell.success.guest.title')"
                    :guest-description="tt('sell.success.guest.description')"
                    :guest-cta="tt('sell.success.guest.cta')"
                />
            </Transition>
        </SellPageHeroBlock>

        <BlockSectionFullWidth :title="tt('sell.sectionSelling.title')" variant="neutral">
            <BlockList
                :items="[
                    { title: tt('sell.sectionSelling.items.item1.label'), description: tt('sell.sectionSelling.items.item1.content') },
                    { title: tt('sell.sectionSelling.items.item2.label'), description: tt('sell.sectionSelling.items.item2.content') },
                    { title: tt('sell.sectionSelling.items.item3.label'), description: tt('sell.sectionSelling.items.item3.content') },
                    { title: tt('sell.sectionSelling.items.item4.label'), description: tt('sell.sectionSelling.items.item4.content') },
                    { title: tt('sell.sectionSelling.items.item5.label'), description: tt('sell.sectionSelling.items.item5.content') },
                ]"
            />
        </BlockSectionFullWidth>

        <BlockSection :title="tt('sell.fleequidBenefits.title')">
            <template #description>
                <i18n-t keypath="sell.fleequidBenefits.description.base" tag="p" scope="global">
                    <template #bold>
                        <span class="font-semibold"> {{ tt('sell.fleequidBenefits.description.bold') }} </span>
                    </template>
                </i18n-t>
            </template>
            <p class="text-base leading-6">{{ tt('sell.fleequidBenefits.p1') }}</p>
            <BlockList
                :items="[
                    tt('sell.fleequidBenefits.items.item1'),
                    tt('sell.fleequidBenefits.items.item2'),
                    tt('sell.fleequidBenefits.items.item3'),
                    tt('sell.fleequidBenefits.items.item4'),
                    tt('sell.fleequidBenefits.items.item5'),
                ]"
                icon="lucide:circle-check"
            />
            <p class="text-base leading-6">{{ tt('sell.fleequidBenefits.p2') }}</p>
        </BlockSection>

        <BlockSectionFullWidth variant="neutral">
            <BlockFleequidStats />
        </BlockSectionFullWidth>

        <BlockSection :title="tt('sell.faq.title')">
            <BaseAccordion
                size="xl"
                :items="[
                    { label: tt('sell.faq.items.item1.label'), slot: 'item1' },
                    { label: tt('sell.faq.items.item2.label'), content: tt('sell.faq.items.item2.content') },
                    { label: tt('sell.faq.items.item3.label'), content: tt('sell.faq.items.item3.content') },
                    { label: tt('sell.faq.items.item4.label'), content: tt('sell.faq.items.item4.content') },
                ]"
            >
                <template #item1>
                    <i18n-t tag="span" scope="global" keypath="sell.faq.items.item1.content.base">
                        <template #link>
                            <NuxtLinkLocale class="text-primary hover:underline" :to="{ name: ROUTE_NAMES.SELL, hash: '#request' }">
                                {{ tt('sell.faq.items.item1.content.link') }}
                            </NuxtLinkLocale>
                        </template>
                    </i18n-t>
                </template>
            </BaseAccordion>
        </BlockSection>

        <BlockSectionFullWidth
            variant="gradient"
            :button="{
                label: tt('sell.sectionEasySelling.cta'),
                trailingIcon: 'lucide:chevron-right',
                to: localePath({ name: ROUTE_NAMES.SELL, hash: '#request' }),
            }"
        >
            <template #title>
                <div>{{ tt('sell.sectionEasySelling.title1') }}</div>
                <div>{{ tt('sell.sectionEasySelling.title2') }}</div>
            </template>
        </BlockSectionFullWidth>
    </div>
</template>
